import { forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';

import { getAll as getAllResearchFields } from 'apis/researchFields.api';
import { create, update } from 'apis/bonusExpenditure.api';
import { createResearchFieldValidationSchema } from 'features/BonusExpenditure/validation';
import { TextInput, DatePicker, Select, NumberInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';
import { mapError, getEndingTimeOfDate } from 'utils/func';

const formOptions = { resolver: yupResolver(createResearchFieldValidationSchema) };

const Form = forwardRef(({ refetch }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [BonusExpenditureControl, setBonusExpenditureControl] = useState(null);

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    formState: { errors, dirtyFields, isDirty },
  } = useForm({ ...formOptions, mode: 'onChange' });

  const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(create);
  const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(update);

  // research fields
  const { data: researchFieldsData } = useQuery(['researchFields'], getAllResearchFields);
  const researchFields = useMemo(
    () =>
      researchFieldsData?.data.map((e) => ({
        ...e,
        name: e.code ? t(`researchFields.${e.code}`) : e.name,
      })) ?? [],
    [researchFieldsData, t]
  );

  // #endregion

  useImperativeHandle(
    ref,
    () => ({
      open: (_bonusExpenditureControl) => {
        setBonusExpenditureControl(_bonusExpenditureControl);
        setVisible(true);
        if (_bonusExpenditureControl.mode === 'CREATE') {
          reset({});
        } else if (_bonusExpenditureControl.mode === 'UPDATE') {
          const { startDate, endDate, ...rest } = _bonusExpenditureControl.value;

          reset({
            ...rest,
            startDate: startDate ? new Date(startDate) : null,
            endDate: endDate ? new Date(endDate) : null,
          });
        }
      },
    }),
    []
  );

  const onSubmit = (data) => {
    const startDate = data?.startDate ? new Date(data.startDate) : null;
    const endDate = data?.startDate ? getEndingTimeOfDate(new Date(data.endDate)) : null;

    if (BonusExpenditureControl.mode === 'CREATE') {
      const newData = {
        ...data,
        startDate,
        endDate,
      };

      mutateCreate(newData, {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('bonusExpenditure.create.createSuccess'));
          reset();
          refetch();
          setVisible(false);
        },
        onError: (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      });
    } else {
      const { id, ...rest } = {
        ...data,
        startDate,
        endDate,
      };
      const newData = Object.keys(dirtyFields)?.reduce((resultObj, current) => {
        // eslint-disable-next-line no-param-reassign
        resultObj[current] = rest[current];
        return resultObj;
      }, {});

      mutateUpdate(
        { id, data: newData },
        {
          onSuccess: () => {
            toast(TOAST.SUCCESS, t('bonusExpenditure.update.updateSuccess'));
            refetch();
            setVisible(false);
          },
          onError: (error) => {
            toast(TOAST.ERROR, mapError(t, error));
          },
        }
      );
    }
  };

  const handleDeadlineTimeChange = async () => {
    await trigger('startDate');
    await trigger('endDate');
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
  };

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };
  // #endregion

  return (
    <Dialog
      header={
        BonusExpenditureControl?.mode === 'CREATE'
          ? t('bonusExpenditure.create.title')
          : t('bonusExpenditure.update.title')
      }
      visible={visible}
      style={{ maxWidth: '700px' }}
      draggable={false}
      onHide={() => setVisible(false)}
    >
      <div className="relative">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={checkKeyDown}
          className="p-fluid flex flex-column"
          autoComplete="off"
        >
          <div className="grid">
            {/* name bonus expenditure  */}
            <div className="col-12">
              <TextInput
                label={t('bonusExpenditure.nameBonus')}
                name="name"
                autoFocus
                control={control}
                errors={errors}
                isRequired
              />
            </div>

            {/*  researchFieldId */}
            <div className="col-12">
              <Select
                label={t('bonusExpenditure.researchField')}
                name="researchFieldId"
                options={researchFields}
                inputField="id"
                renderField="name"
                control={control}
                errors={errors}
                isRequired
              />
            </div>

            {/* expenditure */}
            <div className="col-12">
              <NumberInput
                label={t('bonusExpenditure.expenditure')}
                name="expenditure"
                control={control}
                errors={errors}
                autoFocus
                isRequired
              />
            </div>

            {/* startDate, endDate */}
            <div className="col-6">
              <DatePicker
                label={t('bonusExpenditure.startDate')}
                name="startDate"
                control={control}
                errors={errors}
                onCustomChange={handleDeadlineTimeChange}
                isRequired
              />
            </div>
            <div className="col-6">
              <DatePicker
                label={t('bonusExpenditure.endDate')}
                name="endDate"
                control={control}
                errors={errors}
                onCustomChange={handleDeadlineTimeChange}
                isRequired
              />
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-6">
              <Button
                label={t('bonusExpenditure.cancel')}
                type="button"
                severity="danger"
                onClick={() => {
                  reset();
                  setVisible(false);
                }}
              />
            </div>
            <div className="col-6">
              <Button
                label={
                  BonusExpenditureControl?.mode === 'CREATE'
                    ? t('bonusExpenditure.add')
                    : t('bonusExpenditure.save')
                }
                icon="pi pi-pencil"
                className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                type="submit"
                onClick={checkFormValid}
                disabled={!isDirty || !(Object.keys(dirtyFields)?.length > 0)}
              />
            </div>
          </div>
        </form>
        {(isLoadingCreate || isLoadingUpdate) && <Loading />}
      </div>
    </Dialog>
  );
});

export default Form;

Form.propTypes = {
  refetch: PropTypes.func.isRequired,
};
